import { ActionTree } from 'vuex';
import { nanoid } from 'nanoid';
import { EditorHelper } from '@/editor/helpers/editor.helper';
import {
  ICreateNodeRequest,
  IMoveNodesRequest,
  ISplitNodeRequest,
  IUpdateNodeRequest,
} from '@/editor/api/services/types/requests';
import { ISelection } from '@/editor/types';
import { ENodeType } from '@/editor/types/enums';
import {
  ISplitTextCreateNodeAction,
  ICreateNodeAction,
  IUpdateNodeAction,
  IDeleteNodeAction,
} from '@/editor/editor-block/store/types/actions';
import { IDealroomState } from '@/dealroom/store/dealroom.store';
import { RootState } from '@/store/store';
import { translate } from '@/helpers/translation.helper';
import { EditorBlockService } from '@/editor/api/services/editor-block.service';
import {
  EEditorUiAction,
  editorUiNamespace,
} from '@/editor/editor-block/store/editor-ui.constants';
import {
  EEditorContentAction,
  EEditorContentMutation,
} from '@/editor/editor-block/store/editor-content.constants';
import { IDocumentPage } from '@/types/document-page';

export const actions: ActionTree<IDealroomState, RootState> = {
  async [EEditorContentAction.CreateNode](
    { commit, dispatch, state },
    actionPayload: ICreateNodeAction
  ) {
    const lastBlock: IDocumentPage = EditorHelper.getDocumentPageById(
      state,
      actionPayload.documentPageId
    );

    const {
      documentPageId,
      targetSectionId: sectionId,
      targetRowId: rowId,
      targetCellId: cellId,
      payload,
    } = actionPayload;

    const nodeId: string = nanoid();
    const selectionPayload: ISelection = {
      nodeId,
      cellId,
      rowId,
      sectionId,
      documentPageId,
    };
    const createNodeRequest: ICreateNodeRequest = {
      ...actionPayload,
      payload: { ...payload, nodeId },
    };

    commit(EEditorContentMutation.CreateNode, createNodeRequest);
    dispatch(editorUiNamespace + '/' + EEditorUiAction.SetSelection, selectionPayload, {
      root: true,
    });
    try {
      delete createNodeRequest.payload.srcUrl;
      await EditorBlockService.createNode(createNodeRequest);
    } catch (error) {
      dispatch(EEditorContentAction.CreateNodeFailed, lastBlock);
    }
  },
  [EEditorContentAction.CreateNodeFailed]({ commit, dispatch }, block: IDocumentPage) {
    commit(EEditorContentMutation.ReplaceBlock, block);
    dispatch('alert/showDangerAlert', translate('we-could-not-save-last-edit-please-try-again'), {
      root: true,
    });
  },

  async [EEditorContentAction.MoveNodes]({ commit, dispatch, state }, payload: IMoveNodesRequest) {
    const lastBlock: IDocumentPage = EditorHelper.getDocumentPageById(
      state,
      payload.documentPageId
    );

    commit(EEditorContentMutation.MoveNodes, payload);
    try {
      await EditorBlockService.moveNodes(payload);
    } catch (error) {
      dispatch(EEditorContentAction.MoveNodesFailed, lastBlock);
    }
  },
  [EEditorContentAction.MoveNodesFailed]({ commit, dispatch }, block: IDocumentPage) {
    commit(EEditorContentMutation.ReplaceBlock, block);
    dispatch('alert/showDangerAlert', translate('we-could-not-save-last-edit-please-try-again'), {
      root: true,
    });
  },

  async [EEditorContentAction.SplitTextCreateNode](
    { commit, dispatch, state },
    {
      documentPageId,
      contentId,
      targetSectionId,
      targetRowId,
      targetCellId,
      targetNodeId,
      firstNodeContent,
      lastNodeContent,
      firstNodeFields,
      lastNodeFields,
      payload,
    }: ISplitTextCreateNodeAction
  ) {
    const lastBlock: IDocumentPage = EditorHelper.getDocumentPageById(state, documentPageId);

    const updateFirstNodeRequest: IUpdateNodeRequest = {
      documentPageId,
      contentId,
      targetSectionId,
      targetRowId,
      targetCellId,
      targetNodeId,
      payload: { content: firstNodeContent, fields: firstNodeFields },
    };
    const nodeId = nanoid();
    const createCenterNodeRequest: ICreateNodeRequest = {
      documentPageId,
      contentId,
      targetSectionId,
      targetRowId,
      targetCellId,
      targetNodeId,
      append: true,
      payload: { ...payload, nodeId },
    };
    const lastNodeId = nanoid();
    const createLastNodeRequest: ICreateNodeRequest = {
      documentPageId,
      contentId,
      targetSectionId,
      targetRowId,
      targetCellId,
      targetNodeId: nodeId,
      append: true,
      payload: {
        content: lastNodeContent,
        fields: lastNodeFields,
        nodeType: ENodeType.Text,
        nodeId: lastNodeId,
      },
    };
    const selectionPayload: ISelection = {
      nodeId,
      cellId: targetCellId,
      rowId: targetRowId,
      sectionId: targetSectionId,
      documentPageId,
    };

    commit(EEditorContentMutation.UpdateNode, updateFirstNodeRequest);
    commit(EEditorContentMutation.CreateNode, createCenterNodeRequest);
    commit(EEditorContentMutation.CreateNode, createLastNodeRequest);
    dispatch(editorUiNamespace + '/' + EEditorUiAction.SetSelection, selectionPayload, {
      root: true,
    });
    try {
      delete createCenterNodeRequest.payload.srcUrl;

      const splitPayload: ISplitNodeRequest = {
        documentPageId,
        contentId,
        targetSectionId,
        targetRowId,
        targetCellId,
        targetNodeId,
        split: updateFirstNodeRequest.payload,
        create: [createCenterNodeRequest.payload, createLastNodeRequest.payload],
      };
      await EditorBlockService.splitNode(splitPayload);
    } catch (error) {
      dispatch(EEditorContentAction.SplitTextCreateNodeFailed, lastBlock);
    }
  },
  [EEditorContentAction.SplitTextCreateNodeFailed]({ commit, dispatch }, block: IDocumentPage) {
    commit(EEditorContentMutation.ReplaceBlock, block);
    dispatch('alert/showDangerAlert', translate('we-could-not-save-last-edit-please-try-again'), {
      root: true,
    });
  },

  async [EEditorContentAction.UpdateNode]({ commit, dispatch, state }, payload: IUpdateNodeAction) {
    const lastBlock: IDocumentPage = EditorHelper.getDocumentPageById(
      state,
      payload.documentPageId
    );

    commit(EEditorContentMutation.UpdateNode, payload);
    try {
      delete payload.payload.srcUrl;
      await EditorBlockService.updateNode(payload);
    } catch (error) {
      dispatch(EEditorContentAction.UpdateNodeFailed, lastBlock);
    }
  },
  [EEditorContentAction.UpdateNodeFailed]({ commit, dispatch }, block: IDocumentPage) {
    commit(EEditorContentMutation.ReplaceBlock, block);
    dispatch('alert/showDangerAlert', translate('we-could-not-save-last-edit-please-try-again'), {
      root: true,
    });
  },

  async [EEditorContentAction.DeleteNode]({ commit, dispatch, state }, payload: IDeleteNodeAction) {
    const lastBlock: IDocumentPage = EditorHelper.getDocumentPageById(
      state,
      payload.documentPageId
    );

    commit(EEditorContentMutation.DeleteNode, payload);
    try {
      await EditorBlockService.deleteNode(payload);
    } catch (error) {
      dispatch(EEditorContentAction.DeleteNodeFailed, lastBlock);
    }
  },
  [EEditorContentAction.DeleteNodeFailed]({ commit, dispatch }, block: IDocumentPage) {
    commit(EEditorContentMutation.ReplaceBlock, block);
    dispatch('alert/showDangerAlert', translate('we-could-not-save-last-edit-please-try-again'), {
      root: true,
    });
  },
};
